// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import type { Transaction, LedgerState } from '../../types.js';
import type { ImportedTransaction, ReviewedTransaction } from './types.js';

function dateWithinDays(a: string, b: string, days: number): boolean {
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  return Math.abs(da - db) <= days * 86_400_000;
}

function normalizeDesc(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Build an index of existing transactions for fast duplicate lookup.
 * Returns:
 *  - fitidSet: Set of "accountId|fitid" strings for definitive FITID matches
 *  - exactSet: Set of "accountId|date|amount|desc" strings for exact duplicates
 *  - fuzzyList: array of existing transactions for fuzzy matching
 */
function buildIndex(state: LedgerState, accountId: string) {
  const fitidSet = new Set<string>();
  const exactSet = new Set<string>();
  const fuzzyList: Transaction[] = [];

  for (const tx of state.transactions.values()) {
    if (tx.voided) continue;
    const split = tx.splits.find((s) => s.accountId === accountId);
    if (!split) continue;

    if (tx.fitid) {
      fitidSet.add(`${accountId}|${tx.fitid}`);
    }
    exactSet.add(`${accountId}|${tx.date}|${split.amount}|${normalizeDesc(tx.description)}`);
    fuzzyList.push(tx);
  }

  return { fitidSet, exactSet, fuzzyList };
}

export function detectDuplicates(
  incoming: ImportedTransaction[],
  state: LedgerState,
): ReviewedTransaction[] {
  // Build per-account indices once
  const indices = new Map<string, ReturnType<typeof buildIndex>>();
  const getIndex = (accountId: string) => {
    if (!indices.has(accountId)) {
      indices.set(accountId, buildIndex(state, accountId));
    }
    return indices.get(accountId)!;
  };

  // Also track FITIDs and exact keys among the incoming batch itself to catch
  // duplicates within a single import (e.g. same OFX file imported twice in one session).
  const incomingFitids = new Set<string>();
  const incomingExact = new Set<string>();

  return incoming.map((tx) => {
    const idx = getIndex(tx.accountId);

    // --- 1. FITID match (definitive) ---
    if (tx.fitid) {
      const key = `${tx.accountId}|${tx.fitid}`;
      if (idx.fitidSet.has(key)) {
        return { ...tx, status: 'fitid-duplicate' as const, duplicateReason: 'FITID already imported', selected: false };
      }
      // Within-batch FITID duplicate
      if (incomingFitids.has(key)) {
        return { ...tx, status: 'fitid-duplicate' as const, duplicateReason: 'Duplicate FITID in this import', selected: false };
      }
      incomingFitids.add(key);
    }

    // --- 2. Exact match (same account + date + amount + description) ---
    const descNorm = normalizeDesc(tx.description);
    const exactKey = `${tx.accountId}|${tx.date}|${tx.amount}|${descNorm}`;
    if (idx.exactSet.has(exactKey)) {
      return { ...tx, status: 'exact-duplicate' as const, duplicateReason: 'Same date, amount, and description already exists', selected: false };
    }
    if (incomingExact.has(exactKey)) {
      return { ...tx, status: 'exact-duplicate' as const, duplicateReason: 'Duplicate within this import', selected: false };
    }
    incomingExact.add(exactKey);

    // --- 3. Fuzzy match (same account + amount, date within 3 days, similar description) ---
    for (const existing of idx.fuzzyList) {
      const split = existing.splits.find((s) => s.accountId === tx.accountId);
      if (!split) continue;
      if (split.amount !== tx.amount) continue;
      if (!dateWithinDays(existing.date, tx.date, 3)) continue;
      // Descriptions need not match exactly for fuzzy — same amount within 3 days is enough
      return {
        ...tx,
        status: 'probable-duplicate' as const,
        duplicateReason: `Probable duplicate: existing transaction on ${existing.date} for same amount`,
        selected: false,
      };
    }

    return { ...tx, status: 'new' as const, selected: true };
  });
}
